/** Sign-up, login, phone verification, password reset and the account page. */
import { api, saveDownload } from '../../core/api.js';
import { config } from '../../core/config.js';
import { ROLE_LABEL } from '../../core/constants.js';
import { $ } from '../../core/dom.js';
import { register } from '../../core/events.js';
import { html } from '../../core/html.js';
import { fmtDate } from '../../core/format.js';
import { navigate } from '../../core/router.js';
import { homeFor, session } from '../../core/session.js';
import { session as store } from '../../core/storage.js';
import { every, update } from '../../app/shell.js';
import { backLink, banner, ic, statusTag } from '../../ui/components.js';
import { field, radioField } from '../../ui/forms.js';
import { closeModal, confirmBox, openModal } from '../../ui/modal.js';
import { submitForm } from '../../ui/submit.js';
import { toast } from '../../ui/toast.js';

const PENDING_PHONE = 'vc.pendingPhone';
const OPEN = { auth: false };

/** Only follow same-site paths after login; never an absolute or protocol-relative URL. */
export function safeNext(next) {
  return typeof next === 'string' && next.startsWith('/') && !next.startsWith('//') && !next.startsWith('/login') ? next : '';
}

const maskPhone = (phone) => (phone ? `${phone.slice(0, 3)}${'•'.repeat(Math.max(0, phone.length - 5))}${phone.slice(-2)}` : '');

async function finishLogin(body, next) {
  session.start(body);
  toast(`Welcome, ${body.user.name.split(' ')[0]}`);
  await navigate(safeNext(next) || homeFor(body.user.role), { replace: true });
}

/* ---------- login ---------- */

const login = {
  path: '/login',
  layout: 'auth',
  title: 'Log in',
  guestOnly: true,
  ui: () => ({ error: '' }),
  view(_, ui, ctx) {
    return html`<div class="auth"><div class="card"><h2>Log in to ${config.brandName}</h2><p class="mu">Welcome back.</p>
      <form id="login-form" data-submit="auth.login" novalidate>
        ${field('login-email', 'Email', { type: 'email', name: 'email', ph: 'you@example.com', autocomplete: 'email' })}
        ${field('login-password', 'Password', { type: 'password', name: 'password', err: 'Enter your password.' })}
        <p class="sm" style="text-align:right;margin:-6px 0 8px"><a href="/forgot">Forgot your password?</a></p>
        ${ui.error ? banner(ui.error, 'bad', 'alert') : ''}
        <button class="btn wide lg mt-s" type="submit">Log in</button>
        <input type="hidden" name="next" value="${ctx.query.get('next') || ''}">
      </form>
      <p class="sm mu mt-s">New here? <a href="/join">Create an account</a></p></div></div>`;
  },
};

register({
  'auth.login': (form) => submitForm(
    form,
    async ({ email, password, next }) => {
      update((ui) => { ui.error = ''; });
      const body = await api.post('/auth/login', { ...OPEN, body: { email, password } });
      await finishLogin(body, next);
    },
    {
      onError(error) {
        if (error.code === 'phone_not_verified') {
          const phone = error.details?.phone;
          if (phone) {
            store.set(PENDING_PHONE, phone);
            api.post('/auth/otp/send', { ...OPEN, body: { phone } }).catch(() => {});
            navigate('/verify');
            return;
          }
        }
        update((ui) => { ui.error = error.message; });
      },
    },
  ),
});

/* ---------- choose a role ---------- */

const join = {
  path: '/join',
  layout: 'auth',
  title: 'Join',
  guestOnly: true,
  view() {
    const roles = [
      ['student', 'users', 'Student or job seeker', 'Fresher or experienced. Learn, build skills and get matched to jobs.'],
      ['professional', 'brief', 'Freelancer or working professional', 'Teach, freelance and earn.'],
      ['company', 'build', 'Company', 'Find suitable, prepared talent.'],
      ['startup', 'rocket', 'Startup', 'Find people and co-founders.'],
    ];
    return html`<div class="auth" style="max-width:860px"><h1 style="font-size:34px">How do you want to use ${config.brandName}?</h1><p class="mu">Pick the one that fits you best today.</p>
      <div class="rolepick mt">${roles.map(([role, icon, title, text]) => html`<a class="card click" href="/register/${role}">${ic(icon)}<h3 class="mt-s">${title}</h3><p class="mu" style="margin:0">${text}</p></a>`)}
        <a class="card click" href="/requirements">${ic('clip')}<h3 class="mt-s">Just a requirement</h3><p class="mu" style="margin:0">No account. Tell us what you need and we find people.</p></a></div>
      <p class="sm mu mt">Already a member? <a href="/login">Log in</a></p></div>`;
  },
};

/* ---------- register ---------- */

const REGISTER = {
  student: { label: 'student or job seeker', orgLabel: '', contact: 'Full name' },
  professional: { label: 'freelancer', orgLabel: '', contact: 'Full name' },
  company: { label: 'company', orgLabel: 'Company name', contact: 'Your name' },
  startup: { label: 'startup', orgLabel: 'Startup name', contact: 'Your name' },
};

const registerPage = {
  path: '/register/:role',
  layout: 'auth',
  title: 'Create account',
  guestOnly: true,
  ui: () => ({ error: '', terms: false }),
  view(_, ui, ctx) {
    const role = ctx.params.role;
    const conf = REGISTER[role];
    if (!conf) {
      return html`<div class="auth"><div class="card empty"><h3>Unknown account type</h3><p>Choose one of the account types on the join page.</p><a class="btn" href="/join">Back to join</a></div></div>`;
    }
    const invite = ctx.query.get('invite') || '';
    const isOrg = role === 'company' || role === 'startup';
    return html`<div class="auth">${backLink('/join', 'Change role')}
      <div class="card"><h2>Create your ${conf.label} account</h2><p class="mu">Step 1 of 2: your details. Next we verify your phone.</p>
        <form id="register-form" data-submit="auth.register" data-role="${role}" novalidate>
          ${invite ? html`<input type="hidden" name="invite_token" value="${invite}">${banner('You were invited to join an existing organisation. Use the email address the invitation was sent to.', '', 'info')}` : ''}
          ${role === 'student' ? html`${radioField('seeker_type', 'I am', [['Student', 'Studying or just finished'], ['Job seeker', 'Fresher or experienced, looking for a job']], ctx.query.get('type') === 'job_seeker' ? 'Job seeker' : 'Student')}
            ${field('rg-years', 'Years of work experience', { type: 'number', name: 'experience_years', min: 0, max: 60, req: false, help: 'Leave empty or enter 0 if you are a fresher.' })}` : ''}
          ${isOrg && !invite ? field('rg-org', conf.orgLabel, { name: 'org_name', minlen: 2, autocomplete: 'organization' }) : ''}
          <div class="fgrid">
            ${field('rg-name', conf.contact, { name: 'name', minlen: 2, autocomplete: 'name' })}
            ${field('rg-phone', 'Mobile number', { type: 'tel', name: 'phone', ph: '98765 43210', help: config.otpChannel === 'email' ? 'Used by your trainer and for account recovery. We email you a 6-digit code to confirm it.' : 'We send a 6-digit code to this number.', pattern: '^(\\+?91[\\s-]?)?[6-9]\\d{4}[\\s-]?\\d{5}$', err: 'Enter a 10-digit Indian mobile number.' })}
          </div>
          ${field('rg-email', isOrg ? 'Work email' : 'Email', { type: 'email', name: 'email', autocomplete: 'email' })}
          ${field('rg-pass', 'Password', { type: 'password', name: 'password', autocomplete: 'new-password', minlen: 8, help: 'At least 8 characters. Avoid common passwords.', err: 'Use 8 or more characters.' })}
          <div class="field ${ui.termsBad ? 'bad' : ''}" data-req="terms"><label class="row sm" style="flex-wrap:nowrap;align-items:flex-start;gap:8px"><input type="checkbox" name="accept_terms" ${ui.terms ? 'checked' : ''} data-change="auth.terms"> <span>I agree to the <a href="/terms" target="_blank" rel="noopener">terms</a> and the <a href="/privacy" target="_blank" rel="noopener">privacy notice</a>, and understand that messaging on ${config.brandName} is moderated.</span></label><span class="err" role="alert">Please accept to continue.</span></div>
          ${ui.error ? banner(ui.error, 'bad', 'alert') : ''}
          <button class="btn wide lg mt-s" type="submit">Create account</button>
        </form>
        <p class="sm mu mt-s">Already a member? <a href="/login">Log in</a></p></div></div>`;
  },
};

register({
  'auth.terms': (el) => update((ui) => { ui.terms = el.checked; ui.termsBad = false; }),
  'auth.register': (form) => submitForm(
    form,
    async (values) => {
      const role = form.dataset.role;
      update((ui) => { ui.error = ''; });
      if (!values.accept_terms) {
        update((ui) => { ui.termsBad = true; });
        throw new Error('Please accept the terms to continue.');
      }
      const body = { name: values.name, email: values.email, phone: values.phone, password: values.password, role, accept_terms: true };
      if (values.org_name) body.org_name = values.org_name;
      if (role === 'student') {
        body.seeker_type = values.seeker_type === 'Job seeker' ? 'job_seeker' : 'student';
        if (values.experience_years !== '' && values.experience_years != null) body.experience_years = Number(values.experience_years);
      }
      if (values.invite_token) body.invite_token = values.invite_token;
      await api.post('/auth/register', { ...OPEN, body });
      store.set(PENDING_PHONE, values.phone);
      await navigate('/verify');
    },
    { onError: (error) => update((ui) => { ui.error = error.message; }) },
  ),
});

/* ---------- verify phone ---------- */

const RESEND_SECONDS = 30;

const verify = {
  path: '/verify',
  layout: 'auth',
  title: 'Verify your phone',
  guestOnly: true,
  ui: () => ({ error: '', resendAt: Date.now() + RESEND_SECONDS * 1000, now: Date.now() }),
  enter() {
    every(1000, () => update((ui) => { ui.now = Date.now(); }));
  },
  view(_, ui) {
    const phone = store.get(PENDING_PHONE);
    if (!phone) {
      return html`<div class="auth"><div class="card empty"><h3>Nothing to verify</h3><p>Log in or create an account first.</p><a class="btn" href="/login">Log in</a></div></div>`;
    }
    const wait = Math.max(0, Math.ceil((ui.resendAt - ui.now) / 1000));
    return html`<div class="auth"><div class="card"><h2>Verify your phone</h2>
      <p class="mu">${config.otpChannel === 'email' ? html`We emailed a 6-digit code to the address you signed up with, for the number <b>${maskPhone(phone)}</b>.` : html`We sent a 6-digit code to <b>${maskPhone(phone)}</b>.`} It expires in a few minutes.</p>
      <form id="verify-form" data-submit="auth.verify" novalidate>
        ${field('otp-code', 'Code', { name: 'code', inputmode: 'numeric', autocomplete: 'one-time-code', maxlength: 6, pattern: '^\\d{6}$', ph: '123456', err: 'Enter the 6-digit code.' })}
        ${ui.error ? banner(ui.error, 'bad', 'alert') : ''}
        <button class="btn wide lg mt-s" type="submit">Verify and continue</button>
      </form>
      <p class="sm mu mt-s">Did not get it? ${wait > 0 ? html`You can ask again in ${wait}s.` : html`<button type="button" class="linklike" data-act="auth.resend">Send a new code</button>`}</p>
      <p class="sm mu"><a href="/login">Back to login</a></p></div></div>`;
  },
};

register({
  'auth.verify': (form) => submitForm(
    form,
    async ({ code }) => {
      update((ui) => { ui.error = ''; });
      const phone = store.get(PENDING_PHONE);
      const body = await api.post('/auth/otp/verify', { ...OPEN, body: { phone, code } });
      store.remove(PENDING_PHONE);
      await finishLogin(body, '');
    },
    { onError: (error) => update((ui) => { ui.error = error.message; }) },
  ),
  'auth.resend': async () => {
    const phone = store.get(PENDING_PHONE);
    if (!phone) return;
    await api.post('/auth/otp/send', { ...OPEN, body: { phone } });
    update((ui) => { ui.resendAt = Date.now() + RESEND_SECONDS * 1000; ui.now = Date.now(); });
    toast('A new code is on its way');
  },
});

/* ---------- forgot password (OTP to the verified phone) ---------- */

const forgot = {
  path: '/forgot',
  layout: 'auth',
  title: 'Reset password',
  guestOnly: true,
  ui: () => ({ step: 1, phone: '', error: '' }),
  view(_, ui) {
    if (ui.step === 1) {
      return html`<div class="auth"><div class="card"><h2>Reset your password</h2><p class="mu">Enter the mobile number on your account and we will ${config.otpChannel === 'email' ? 'email' : 'text'} you a code.</p>
        <form data-submit="auth.forgot" novalidate>
          ${field('fg-phone', 'Mobile number', { type: 'tel', name: 'phone', ph: '98765 43210', err: 'Enter your mobile number.' })}
          ${ui.error ? banner(ui.error, 'bad', 'alert') : ''}
          <button class="btn wide lg mt-s" type="submit">Send code</button>
        </form><p class="sm mu mt-s"><a href="/login">Back to login</a></p></div></div>`;
    }
    return html`<div class="auth"><div class="card"><h2>Choose a new password</h2><p class="mu">If that number has an account, a code is on its way ${config.otpChannel === 'email' ? 'to the email on that account' : html`to <b>${maskPhone(ui.phone)}</b>`}.</p>
      <form data-submit="auth.reset" novalidate>
        <input type="hidden" name="phone" value="${ui.phone}">
        ${field('fg-code', 'Code', { name: 'code', inputmode: 'numeric', autocomplete: 'one-time-code', maxlength: 6, pattern: '^\\d{6}$', err: 'Enter the 6-digit code.' })}
        ${field('fg-pass', 'New password', { type: 'password', name: 'password', autocomplete: 'new-password', minlen: 8, err: 'Use 8 or more characters.' })}
        ${ui.error ? banner(ui.error, 'bad', 'alert') : ''}
        <button class="btn wide lg mt-s" type="submit">Change password</button>
      </form></div></div>`;
  },
};

register({
  'auth.forgot': (form) => submitForm(
    form,
    async ({ phone }) => {
      await api.post('/auth/password/forgot', { ...OPEN, body: { phone } });
      update((ui) => { ui.step = 2; ui.phone = phone; ui.error = ''; });
    },
    { onError: (error) => update((ui) => { ui.error = error.message; }) },
  ),
  'auth.reset': (form) => submitForm(
    form,
    async ({ phone, code, password }) => {
      await api.post('/auth/password/reset', { ...OPEN, body: { phone, code, password } });
      toast('Password changed. Log in with the new one.');
      await navigate('/login', { replace: true });
    },
    { onError: (error) => update((ui) => { ui.error = error.message; }) },
  ),
});

/* ---------- account and privacy (all roles) ---------- */

function accountPage(role) {
  return {
    path: `/${role}/account`,
    roles: [role],
    title: 'Account and privacy',
    nav: 'account',
    ui: () => ({ error: '' }),
    view(_, ui) {
      const u = session.user;
      return html`<div class="grid g2">
        <div class="card"><h3>Your account</h3>
          <table><tr><td class="mu">Name</td><td>${u.name}</td></tr><tr><td class="mu">Email</td><td>${u.email}</td></tr>
          <tr><td class="mu">Mobile</td><td>${u.phone} ${u.phone_verified ? statusTag('verified') : statusTag('unverified')}</td></tr>
          <tr><td class="mu">Account type</td><td>${ROLE_LABEL[u.role]}${u.org_name ? html` · ${u.org_name}` : ''}</td></tr>
          <tr><td class="mu">Member since</td><td>${fmtDate(u.created_at)}</td></tr></table></div>
        <div class="card"><h3>Change password</h3>
          <form data-submit="account.password" novalidate>
            ${field('cp-old', 'Current password', { type: 'password', name: 'current_password', err: 'Enter your current password.' })}
            ${field('cp-new', 'New password', { type: 'password', name: 'new_password', autocomplete: 'new-password', minlen: 8, err: 'Use 8 or more characters.' })}
            ${ui.error ? banner(ui.error, 'bad', 'alert') : ''}
            <button class="btn" type="submit">Change password</button>
          </form></div>
        <div class="card"><h3>Your data</h3><p class="mu">Download a copy of everything we hold about you, as a JSON file.</p>
          <button type="button" class="btn ghost" data-act="account.export">${ic('dl')} Download my data</button></div>
        <div class="card"><h3>Delete my account</h3><p class="mu">This removes your personal details and signs you out everywhere. Orders and invoices are kept because tax law requires it. This cannot be undone.</p>
          <button type="button" class="btn danger" data-act="account.delete">Delete my account</button></div></div>`;
    },
  };
}

register({
  'account.password': (form) => submitForm(
    form,
    async (values) => {
      // The server signs every other device out and hands this one fresh tokens.
      session.start(await api.post('/me/password', { body: values }));
      toast('Password changed');
      form.reset();
      update((ui) => { ui.error = ''; });
    },
    { onError: (error) => update((ui) => { ui.error = error.message; }) },
  ),
  'account.export': () => saveDownload('/me/export', 'my-vconnect-data.json'),
  'account.delete': async () => {
    const sure = await confirmBox({ title: 'Delete your account?', text: 'Your personal details will be erased and you will be signed out. This cannot be undone.', label: 'Continue', danger: true });
    if (!sure) return;
    openModal({
      title: 'Confirm with your password',
      body: html`<form id="delete-form" data-submit="account.deleteNow" novalidate>${field('del-pass', 'Password', { type: 'password', name: 'password', err: 'Enter your password.' })}<p class="err-line xs" id="delete-error" role="alert"></p></form>`,
      foot: html`<button type="button" class="btn ghost" data-act="modal.close">Cancel</button><button type="submit" form="delete-form" class="btn danger">Delete my account</button>`,
    });
  },
  'account.deleteNow': (form) => submitForm(
    form,
    async ({ password }) => {
      await api.delete('/me', { body: { password } });
      closeModal(false);
      session.clear();
      toast('Your account was deleted');
      await navigate('/', { replace: true });
    },
    { onError: (error) => { $('#delete-error').textContent = error.message; } },
  ),
});

export const routes = [login, join, registerPage, verify, forgot, ...['student', 'professional', 'company', 'startup', 'admin'].map(accountPage)];
